import { ShieldCheck, Bell, UserCheck, Scale, RefreshCw, Network } from 'lucide-react';
import { Hero, SectionHeader, FeatureGrid, Slab, MockVisual, CTABanner } from '@/components/marketing/primitives.jsx';

const capabilities = [
  {
    icon: Bell,
    title: 'Challenge in seconds',
    description:
      'Institutional partners can raise a challenge on a transaction or account event; the owning individual receives an in-app prompt and can confirm or deny.',
  },
  {
    icon: UserCheck,
    title: 'Identity state machine',
    description:
      'Every person has an enforced state: clean, under review, contested, compromised, sealed. Transitions require named reviewers and logged reasons.',
  },
  {
    icon: Network,
    title: 'Institutional partners',
    description:
      'Banks, issuers, and exchanges subscribe to scoped channels for their institution code. No cross-tenant visibility, ever.',
  },
  {
    icon: RefreshCw,
    title: 'Dispute workflow',
    description:
      'Individuals contest challenges through a guided flow. Each step is signed. Outcomes feed back into the state machine.',
  },
  {
    icon: Scale,
    title: 'Escalation board',
    description:
      'Supervisors see the cases that need human attention — pattern anomalies, cross-institution signals, high-severity holds.',
  },
  {
    icon: ShieldCheck,
    title: 'Integrity chain per identity',
    description:
      'Every event — challenge raised, response given, state changed — is chained per identity and mirrored into the master audit log.',
  },
];

export default function IFPSPage() {
  return (
    <>
      <Hero
        eyebrow="IFPS — Identity Fraud Prevention System"
        title="Coordinated identity integrity across institutional partners."
        description="A shared challenge/response layer between individuals and the banks, issuers, and agencies that serve them — with state machines that make sure nothing falls through the cracks."
        primaryCta={{ to: '/for-partners', label: 'Partner onboarding' }}
        secondaryCta={{ to: '/contact', label: 'Talk to us' }}
      />

      <section className="py-20 md:py-24">
        <div className="container-wide">
          <SectionHeader
            eyebrow="Capabilities"
            title="Real-time, bi-directional, auditable."
          />
          <div className="mt-12">
            <FeatureGrid items={capabilities} columns={3} />
          </div>
        </div>
      </section>

      <Slab
        eyebrow="State machine"
        title="No ambiguity about where an identity stands."
        description="IFPS enforces a strict state machine per individual. Transitions only fire on named reviewer decisions and are mirrored into the integrity chain. Every 'unknown' is a failure to log, not a valid state."
        bullets={[
          'States: clean → under_review → contested → compromised → sealed',
          'Only reviewers with ifps_supervisor role can confirm state changes',
          'Escalations auto-surface to the supervisor board',
          'Sealed identities require a judicial warrant to unseal',
        ]}
        image={<MockVisual accent="sky" lines={5} />}
      />

      <Slab
        reverse
        eyebrow="Privacy model"
        title="Institutions see only what they need."
        description="The institution_code claim on a partner user scopes their view to their own signals. Cross-tenant queries are impossible through the API — it's enforced at the controller, the query, and the WebSocket channel."
        bullets={[
          'All partner endpoints require role:institutional_partner + matching institution_code',
          'Private WebSocket channel authorisation validates the claim on every subscribe',
          'Challenge payloads do not include PII beyond what the partner supplied',
        ]}
        image={<MockVisual accent="emerald" lines={5} />}
      />

      <CTABanner
        title="Integrate your institution with IFPS."
        description="Banks, issuers, exchanges, and agency identity teams can join via tiered onboarding and the Interoperability Sandbox."
        primaryCta={{ to: '/contact', label: 'Start a partnership' }}
        secondaryCta={{ to: '/for-partners', label: 'Tiers & access levels' }}
      />
    </>
  );
}
